import {Badge, Button, view, PropertyList, PropertyListItem} from '@sail/ui';
import {
  InlineDatumReactComponentProps,
  useAnalytics,
} from '@stripe-internal/inline-data';
import {defineMessages, FormattedMessage, useIntl} from 'react-intl';
import {
  Card,
  TaskBody,
  widgetLinkAction,
} from '@stripe-internal/conversations-ui';
import {
  InlineLinkClickHandler,
  InlineLinkProps,
} from '../helpers/InlineLinkClickHandler';
import {CardForNewDesign} from '../helpers/CardForNewDesign';

export type ValueProps = {
  task_detail?: string;
  badge?: string;
  task_info?: string;
  deadline?: number;
  estimated_review_completion_date?: number;
  impact?: string[];
  account_paused?: boolean;
  paused_date?: number;
  cta_link?: string;
  cta_copy?: string;
  is_in_review?: string;
  template_pairs?: {
    [key: string]: string;
  };
  universal_help_new_design?: boolean;
};

const messages = defineMessages({
  taskInReviewCTA: {
    id: 'mse_inline_data.universal_help_verification_summary_response.task_cta.in_review',
    description: 'CTA text shown when a verification task is under review.',
    defaultMessage: 'View Details',
  },
  capabilityPaused: {
    id: 'mse_inline_data.universal_help_verification_summary.capability_paused',
    description: 'Message indicating that an account capability is paused.',
    defaultMessage: 'Paused',
  },
  capabilityPausedSince: {
    id: 'mse_inline_data.universal_help_verification_summary.capability_paused_since',
    description:
      'Message indicating that an account capability has been paused since a date.',
    defaultMessage: 'Paused since {date}',
  },
  impactPayouts: {
    id: 'mse_inline_data.universal_help_verification_summary.impact.payouts',
    description: 'Impact of a verification task on payouts.',
    defaultMessage: 'Payouts',
  },
  impactCharges: {
    id: 'mse_inline_data.universal_help_verification_summary.impact.charges',
    description: 'Impact of a verification task on charges.',
    defaultMessage: 'Charges',
  },
  impactHeader: {
    id: 'mse_inline_data.universal_help_verification_summary.impact.heading',
    description: 'Heading a list of impacted capabilities.',
    defaultMessage: 'Impact',
  },
  deadlineHeader: {
    id: 'mse_inline_data.universal_help_verification_summary.deadline.heading',
    description: 'Heading for the task deadline.',
    defaultMessage: 'Deadline',
  },
  estimatedCompletionHeader: {
    id: 'mse_inline_data.universal_help_verification_summary.estimated_completion.heading',
    description: 'Heading for the estimated review completion date.',
    defaultMessage: 'Estimated review completion',
  },
  inReviewEtaMessageTitle: {
    id: 'mse_inline_data.universal_help_verification_summary.in_review_eta_title',
    description:
      'Title Message shown when a verification is in review with an estimated completion date. No merchant action needed.',
    defaultMessage: 'Your information is being reviewed',
  },
  inReviewEtaMessage: {
    id: 'mse_inline_data.universal_help_verification_summary.in_review_eta_message',
    description:
      'Message shown when a verification is in review with an estimated completion date. No merchant action needed.',
    defaultMessage:
      "We're reviewing your information and expect to finish by {date}. No action needed from you.",
  },
});

export const MerchantIssueSummary = (props: InlineDatumReactComponentProps) => {
  const {datum} = props;
  const value = datum.value as ValueProps;
  const analytics = useAnalytics();

  const {locale} = useIntl();
  const localizeDate = (date: number) =>
    Intl.DateTimeFormat(locale, {
      month: 'short',
      day: 'numeric',
    }).format(new Date(date * 1000));

  const localizeETADate = (date: number) =>
    Intl.DateTimeFormat(locale, {
      month: 'long',
      day: 'numeric',
    }).format(new Date(date * 1000));

  const {
    task_detail: taskDetail,
    badge,
    task_info: taskInfo,
    deadline,
    estimated_review_completion_date: estimatedReviewCompletionDate,
    impact,
    account_paused: accountPaused,
    paused_date: pausedDate,
    cta_link: ctaLink,
    cta_copy: ctaCopy,
    is_in_review: isInReview,
    template_pairs: templatePairs,
    universal_help_new_design: universalHelpNewDesign,
  } = value;

  let pausedMessage;
  if (pausedDate) {
    const localizedPausedDate = localizeDate(pausedDate);

    pausedMessage = (
      <FormattedMessage
        {...messages.capabilityPausedSince}
        values={{date: localizedPausedDate}}
      />
    );
  } else if (accountPaused === true) {
    pausedMessage = <FormattedMessage {...messages.capabilityPaused} />;
  }

  let localizedDeadline;
  if (deadline) {
    localizedDeadline = localizeDate(deadline);
  }

  const showInReviewMessage = !!(isInReview && estimatedReviewCompletionDate);

  const impactedCapabilities = impact
    ? impact
        .flatMap((capabilityEnum: string) => {
          let message;
          if (capabilityEnum === 'payouts') {
            message = messages.impactPayouts;
          } else if (capabilityEnum === 'charges') {
            message = messages.impactCharges;
          }

          return message && <FormattedMessage {...message} />;
        })
        .filter((message) => !!message)
        .map((formattedMessage, i, _array) => (
          <>
            {i ? ', ' : ''}
            {formattedMessage}
          </>
        ))
    : [];

  if (showInReviewMessage) {
    return (
      <view.div>
        <view.div css={{stack: 'y', gap: 'small', fontWeight: 'bold'}}>
          <FormattedMessage {...messages.inReviewEtaMessageTitle} />
        </view.div>
        <br />
        <view.div css={{stack: 'y', gap: 'small'}}>
          <FormattedMessage
            {...messages.inReviewEtaMessage}
            values={{date: localizeETADate(estimatedReviewCompletionDate!)}}
          />
        </view.div>
      </view.div>
    );
  }

  return (
    <CardForNewDesign
      universalHelpNewDesign={universalHelpNewDesign}
      CardComp={Card}
    >
      <view.div css={{stack: 'y', gap: 'large'}}>
        <view.div css={{stack: 'y', gap: 'xsmall'}}>
          <view.div css={{stack: 'y', gap: 'small'}}>
            <view.div>
              <Badge
                data-testid="task-badge"
                type={isInReview ? 'neutral' : 'warning'}
              >
                {badge}
              </Badge>
            </view.div>
            <view.div css={{font: 'body.small.emphasized'}}>
              {taskInfo}
            </view.div>
          </view.div>

          {taskDetail ? (
            <InlineLinkClickHandler
              onClick={({
                href,
                innerText,
                parentInnerText,
              }: InlineLinkProps) => {
                analytics.action('uh_merchant_issue_summary_link', {
                  uh_verifications_issue_summary_link_href: href,
                  uh_verifications_issue_summary_link_copy: innerText,
                  uh_verifications_issue_summary_link_context: parentInnerText,
                });
              }}
            >
              <TaskBody template={taskDetail} templatePairs={templatePairs} />
            </InlineLinkClickHandler>
          ) : null}
        </view.div>
        <view.div>
          <PropertyList css={{gap: 'large', font: 'label.small'}}>
            {localizedDeadline ? (
              <PropertyListItem
                label={<FormattedMessage {...messages.deadlineHeader} />}
                value={localizedDeadline}
              />
            ) : null}
            {impactedCapabilities.length > 0 ? (
              <PropertyListItem
                data-testid="task-impact"
                label={<FormattedMessage {...messages.impactHeader} />}
                value={impactedCapabilities}
              />
            ) : null}
          </PropertyList>
          {pausedMessage ? (
            <view.span
              css={{
                font: 'label.small.emphasized',
                color: 'feedback.attention',
              }}
            >
              {pausedMessage}
            </view.span>
          ) : null}
        </view.div>

        {ctaCopy && ctaLink ? (
          <Button
            data-testid="task-cta"
            type={isInReview ? 'secondary' : 'primary'}
            css={{width: 'fill'}}
            uses={[widgetLinkAction()]}
            href={ctaLink}
            onPress={() => {
              analytics.action('uh_merchant_issue_summary_button', {
                ctaLink,
                ctaCopy,
              });
            }}
          >
            {isInReview ? (
              <FormattedMessage {...messages.taskInReviewCTA} />
            ) : (
              ctaCopy
            )}
          </Button>
        ) : null}
      </view.div>
    </CardForNewDesign>
  );
};
